import React, { Fragment, useEffect, useMemo, useState } from "react";
import Header from "../../components/header/Header";
import { useParams } from "react-router-dom";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import CtaSection from "../../components/CtaSection/CtaSection";
import axios from "axios";
import ScreenshotsCarousel from "../../components/crousal/crousal";
import Testimonial from "../../components/ProcessAndMetrics/Testimonial";
import icon from "../../images/icons/icon_check_3.svg";
import { Helmet } from "react-helmet-async";
import { useSeo } from "../../hooks/useSeo";

const formatDMY = (value) => {
  const d = new Date(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const staticMeta = {
  title: "Case Study | RashTech Info",
  description:
    "In-depth case study detailing client challenges, approach, solution, and results delivered by RashTech Info.",
  url: "https://rashtechinfo.com/case-study",
  image: "https://rashtechinfo.com/og-image.png",
  siteName: "RashTech Info",
  keywords:
    "case study, software development, results, metrics, process, RashTech Info",
  themeColor: "#0A0D1C",
};

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: staticMeta.siteName,
  url: "https://rashtechinfo.com/",
  logo: "https://rashtechinfo.com/logo.png",
};

const CaseStudySinglePage = () => {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState([]);
  const decodedTitle = encodeURIComponent(id.replace(/-/g, " "));

  // Load SEO
  const { seo } = useSeo("/getSeo");

  // Compute SEO with case study specifics
  const computed = useMemo(() => {
    const dynamic = {
      title: seo?.title,
      description: seo?.metaDescription,
      keywords: seo?.metaKeywords,
      canonicalUrl: seo?.canonicalUrl,
    };

    const title =
      (caseStudy?.clientName
        ? `${caseStudy.clientName} | Case Study | RashTech Info`
        : null) || dynamic.title || staticMeta.title;

    const description =
      caseStudy?.projectBrief ||
      caseStudy?.clientIntro ||
      dynamic.description ||
      staticMeta.description;

    return {
      title,
      description,
      keywords: dynamic.keywords || staticMeta.keywords,
      url:
        dynamic.canonicalUrl ||
        (decodedTitle ? `https://rashtechinfo.com/case_study_details/${decodedTitle}` : staticMeta.url),
      image: staticMeta.image,
      siteName: staticMeta.siteName,
      themeColor: staticMeta.themeColor,
    };
  }, [seo, caseStudy, decodedTitle]);

  useEffect(() => {
    const fetchCaseStudy = async () => {
      try {
        const response = await axios.get(
          `https://api.rashtechinfo.com/casestudy/${decodedTitle}`
        );
        setCaseStudy(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchCaseStudy();
  }, [decodedTitle]);

  const ClickHandler = () => {
    window.scrollTo(10, 0);
  };

  return (
    <Fragment>
      <Helmet>
        <title>{computed.title}</title>
        <meta name="description" content={computed.description} />
        {computed.keywords && <meta name="keywords" content={computed.keywords} />}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content={computed.themeColor} />
        {computed.url && <link rel="canonical" href={computed.url} />}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={computed.siteName} />
        <meta property="og:title" content={computed.title} />
        <meta property="og:description" content={computed.description} />
        {computed.url && <meta property="og:url" content={computed.url} />}
        {computed.image && <meta property="og:image" content={computed.image} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={computed.title} />
        <meta name="twitter:description" content={computed.description} />
        {computed.image && <meta name="twitter:image" content={computed.image} />}

        <script type="application/ld+json">
          {JSON.stringify(orgJsonLd)}
        </script>
        {/* Breadcrumbs */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://rashtechinfo.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Case Studies",
                item: "https://rashtechinfo.com/case-study",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: caseStudy?.clientName || "Case Study",
                item: computed.url,
              },
            ],
          })}
        </script>
      </Helmet>

      <Header />
      <main className="page_content about-page">
        <PageTitle
          pageTitle={caseStudy.caseStudyTitle}
          pagesub={"Details"}
          pageTop={"Case Study"}
        />
        <section className="portfolio_details_section section_space bg-light">
          <div className="container">
            <div className="details_item_image">
              <img
                src={`https://api.rashtechinfo.com/uploads/${caseStudy.bannerImage}`}
                alt="Case Study Item"
              />
            </div>
            <h2 className="details_item_title">Project Description</h2>
            <p className="mb-4">{caseStudy.clientIntro}</p>
            <p>{caseStudy.projectBrief}</p>
            <hr />
            <ul className="portfolio_details_info_list icon_list unordered_list justify-content-lg-between mb-5">
              <li>
                <span className="icon_list_text">
                  <strong className="text-dark text-uppercase">
                    Industry:{" "}
                  </strong>
                  {caseStudy.clientIndustry}
                </span>
              </li>
              <li>
                <span className="icon_list_text">
                  <strong className="text-dark text-uppercase">Client: </strong>
                  {caseStudy.clientName}
                </span>
              </li>
              <li>
                <span className="icon_list_text">
                  <strong className="text-dark text-uppercase">
                    Updation Date:{" "}
                  </strong>
                  {formatDMY(caseStudy.createdAt)}
                </span>
              </li>
            </ul>

            <h3 className="details_item_info_title pt-4">Project Challenges</h3>
            <p>{caseStudy.challenge}</p>

            <div className="row mb-4">
              <div className="col-lg-5">
                <ul className="icon_list unordered_list_block">
                  {Array.isArray(caseStudy.techStack) &&
                    caseStudy.techStack.map((item, idx) => (
                      <li key={idx}>
                        <span className="icon_list_icon">
                          <img src={icon} alt="Check SVG Icon" />
                        </span>
                        <span className="icon_list_text">{item}</span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            <h3 className="details_item_info_title pt-4">Process & Metrics</h3>

            <div className="process-steps process-steps--light">
              {(caseStudy?.processSteps || [])
                .filter((s) => s && (s.title || s.description || s.image))
                .map((step, i) => (
                  <div
                    key={step?._id || i}
                    className="process-step process-step--light"
                  >
                    <div className="process-step-media">
                      {step?.image ? (
                        <img
                          src={`https://api.rashtechinfo.com/uploads/${step.image}`}
                          alt={step?.title || `Step ${i + 1}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="process-step-placeholder">{i + 1}</div>
                      )}
                    </div>
                    <div className="process-step-content">
                      {step?.title && (
                        <h4 className="process-step-title">{step.title}</h4>
                      )}
                      {step?.description && (
                        <p className="process-step-desc">{step.description}</p>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {caseStudy?.resultsSummary ? (
              <div className="results-summary results-summary--light">
                <span className="results-icon" aria-hidden="true">
                  ✓
                </span>
                <p>{caseStudy.resultsSummary}</p>
              </div>
            ) : null}

            {Array.isArray(caseStudy?.metrics) && caseStudy.metrics.length > 0 ? (
              <div className="metrics-grid metrics-grid--light">
                {caseStudy.metrics
                  .filter((m) => m && (m.label || m.value))
                  .map((m, idx) => (
                    <div key={m?._id || idx} className="metric-item">
                      {m?.label && (
                        <span className="metric-label">{m.label}</span>
                      )}
                      {m?.value && (
                        <span className="metric-value">{m.value}</span>
                      )}
                    </div>
                  ))}
              </div>
            ) : null}

            <h3 className="details_item_info_title pt-4">Project Screenshot</h3>
            <div className="CaseStudy_ScreenShot">
              <ScreenshotsCarousel caseStudy={caseStudy} />
            </div>

            <h3 className="details_item_info_title pt-4">Solution</h3>
            <p>{caseStudy.solution}</p>

            <h3 className="details_item_info_title pt-4">Client Testimonial</h3>
            <div>
              <Testimonial
                testimonial={{
                  quote: caseStudy?.testimonial?.quote,
                  author: caseStudy?.testimonial?.author,
                }}
              />
            </div>
          </div>
        </section>
        <CtaSection />
      </main>
      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default CaseStudySinglePage;
